import { Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanDeactivate, UrlTree } from '@angular/router';
import { PaceState } from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { catchError, filter, first, map } from 'rxjs/operators';
import { loadForm, saveForm, unloadForm } from './store/actions/form.actions';
import { getForm, getFormLoadingStatus } from './store/selectors/form.selector';
import { FormIdentifier } from './types/form-identifier';
import { FormsConstants } from './types/forms-constants';
import { FormUtilities } from './utilities/form-utilities';
import FORMSTATUS = FormsConstants.FORMSTATUS;

@Injectable({
    providedIn: 'root',
})
export class PaceForms2Guard implements CanActivate, CanDeactivate<unknown> {
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly store: Store<PaceState>,
    ) {}

    public canActivate(
        route: ActivatedRouteSnapshot,
    ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
        return this.store.select(getForm).pipe(
            map((currentForm) => {
                const params = route.params;

                const form: FormIdentifier = {
                    equipmentId: params.equipmentid,
                    formId: params.formid,
                    materialId: params.materialid,
                };
                const language: string = params.language;

                const isRequestedFormLoaded = currentForm.loadingStatus === FormsConstants.FORM_LOADING_STATUS.LOADED;

                if (!currentForm.errorDuringLoading && !isRequestedFormLoaded && !currentForm.isLoading) {
                    const notification = { frontId: params.frontid, maNum: params.manum, qmNum: params.qmnum };
                    const status = params.lockmode as FORMSTATUS;
                    const formType = FormUtilities.getFormTypeFromId(form.formId);
                    this.store.dispatch(loadForm({ form, notification, status, formType, language }));
                }

                return { isRequestedFormLoaded, error: currentForm.errorDuringLoading };
            }),
            filter(({ isRequestedFormLoaded, error }) => isRequestedFormLoaded || error),
            map(({ error }) => !error),
            first(),
        );
    }

    public canDeactivate(): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
        return this.store.select(getFormLoadingStatus).pipe(
            map((loadingStatus) => {
                // if the form was aborted, the form will be set to the initial form state (isEdited=false) in the reducer before the
                // navigation is started. In this case, no saving will take place.

                // only save the form if it was edited
                if (loadingStatus === FormsConstants.FORM_LOADING_STATUS.EDITED) {
                    this.store.dispatch(saveForm());
                }

                this.store.dispatch(unloadForm());
                // saveForm action updates the isEdited flag and returns new value from selector
                // the guard then only continues after save is completed
                return loadingStatus !== FormsConstants.FORM_LOADING_STATUS.EDITED;
            }),
            filter((canDeactivate) => canDeactivate),
            first(),
            catchError((error) => {
                this.log.error(`PaceForms2Guard: canDeactivate: error: ${JSON.stringify(error)}`);
                throw error;
            }),
        );
    }
}
